"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { categoryMeta, getCategoryColor } from "./data";
import type { Post } from "./types";
import {
  CategoryBadge as SharedCategoryBadge,
  PostListItem as SharedPostListItem,
  HeroFeaturedCard as SharedHeroFeaturedCard,
  ArchiveNewsletterForm,
} from "@/components/sections/editorial/_shared/archive/parts";
export { Pagination } from "@/components/ui/pagination";

export function HeroVisual({ color }: { color: string }) {
  return (
    <div
      className="relative h-full w-full overflow-hidden"
      style={{ background: "#0d0d0f" }}
      aria-hidden="true"
    >
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 560 360"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
      >
        <defs>
          <pattern id="smallGrid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path
              d="M 28 0 L 0 0 0 28"
              fill="none"
              stroke="white"
              strokeWidth="0.3"
              strokeOpacity="0.07"
            />
          </pattern>
          <pattern id="bigGrid" width="112" height="112" patternUnits="userSpaceOnUse">
            <rect width="112" height="112" fill="url(#smallGrid)" />
            <path
              d="M 112 0 L 0 0 0 112"
              fill="none"
              stroke="white"
              strokeWidth="0.6"
              strokeOpacity="0.10"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#bigGrid)" />
        <path
          d="M 20 300 C 60 300 80 260 100 220 C 120 180 130 340 160 280 C 190 220 200 100 260 80 C 320 60 350 180 400 160 C 440 145 480 130 540 110"
          stroke={color}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.85"
        />
        <path
          d="M 20 320 C 80 320 120 290 180 270 C 240 250 280 230 340 220 C 400 210 460 200 540 185"
          stroke={color}
          strokeWidth="0.8"
          strokeLinecap="round"
          fill="none"
          opacity="0.20"
        />
        <line
          x1="20"
          y1="20"
          x2="20"
          y2="340"
          stroke="white"
          strokeWidth="0.6"
          strokeOpacity="0.12"
        />
        <line
          x1="20"
          y1="340"
          x2="540"
          y2="340"
          stroke="white"
          strokeWidth="0.6"
          strokeOpacity="0.12"
        />
        <circle cx="260" cy="80" r="4" fill={color} opacity="0.9" />
        <circle cx="260" cy="80" r="9" fill={color} opacity="0.15" />
      </svg>
      <div className="text-foreground/[18%] absolute right-7 bottom-6 flex items-center gap-2.5 select-none">
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path
            d="M11 2 L19.5 7 L19.5 15 L11 20 L2.5 15 L2.5 7 Z"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
          />
          <text
            x="11"
            y="15"
            textAnchor="middle"
            fontSize="9"
            fontWeight="700"
            fill="currentColor"
            fontFamily="sans-serif"
          >
            P
          </text>
        </svg>
        <span className="text-sm font-semibold tracking-wide">Proxidize</span>
      </div>
      <div
        className="absolute"
        style={{
          left: "38%",
          top: "8%",
          width: 160,
          height: 120,
          background: `radial-gradient(ellipse at center, ${color}22, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

export function CategoryBadge({ category }: { category: string }) {
  const color = getCategoryColor(category);
  const icon = categoryMeta[category]?.icon;
  return <SharedCategoryBadge category={category} color={color} icon={icon} />;
}

export function PostListItem({ post, index }: { post: Post; index: number }) {
  const color = getCategoryColor(post.category);
  return (
    <SharedPostListItem
      post={post}
      index={index}
      color={color}
      readMoreLabel="Read post"
      ns="blog"
    />
  );
}

export function HeroFeaturedCard({ post }: { post: Post }) {
  const color = getCategoryColor(post.category);
  return (
    <SharedHeroFeaturedCard
      post={post}
      color={color}
      readMoreLabel="Read post"
      visual={<HeroVisual color={color} />}
      categoryBadge={<CategoryBadge category={post.category} />}
    />
  );
}

export function BlogArchiveNewsletterForm() {
  return <ArchiveNewsletterForm ariaLabel="Newsletter signup" />;
}

export function CategoryFilterSidebar({
  categories,
  activeCategory,
  onCategoryChange,
}: {
  categories: string[];
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
}) {
  const prefersReducedMotion = useReducedMotion();
  return (
    <div className="bg-card border-border overflow-hidden rounded-xl border">
      <div className="border-border border-b px-5 py-4">
        <h3 className="text-foreground text-lg font-semibold">Categories</h3>
      </div>
      <ul className="max-h-[420px] space-y-1 overflow-y-auto px-5 py-3">
        {categories.map((cat, i) => {
          const isActive = activeCategory === cat;
          const icon = cat !== "All" ? categoryMeta[cat]?.icon : null;
          return (
            <motion.li
              key={cat}
              initial={prefersReducedMotion ? false : { opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, delay: 0.25 + i * 0.04, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                onClick={() => onCategoryChange(cat)}
                aria-pressed={isActive}
                style={{ ["--cat-color" as string]: "var(--brand-green)" }}
                className={
                  "group relative flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium " +
                  "cursor-pointer transition-all duration-200 ease-out " +
                  "focus-visible:ring-2 focus-visible:ring-[color:var(--cat-color)]/40 focus-visible:outline-none " +
                  (isActive
                    ? "bg-[color:var(--cat-color)]/[0.12] text-[color:var(--cat-color)]"
                    : "text-muted-foreground hover:translate-x-0.5 hover:bg-[color:var(--cat-color)]/[0.06] hover:text-[color:var(--cat-color)]")
                }
              >
                <span className="flex items-center gap-2">
                  <span
                    className={
                      "inline-flex items-center justify-center transition-colors duration-200 " +
                      (isActive
                        ? "text-[color:var(--cat-color)]"
                        : "text-muted-foreground group-hover:text-[color:var(--cat-color)]")
                    }
                  >
                    {icon}
                  </span>
                  {cat}
                </span>
              </button>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}

export function BlogPostsLoader({ label = "Loading posts" }: { label?: string }) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center gap-4"
    >
      <div
        className={prefersReducedMotion ? "h-12 w-12" : "h-12 w-12 animate-spin"}
        style={prefersReducedMotion ? undefined : { animationDuration: "0.9s" }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 48 48" fill="none" className="h-full w-full">
          <circle
            cx="24"
            cy="24"
            r="20"
            stroke="color-mix(in srgb, var(--foreground) 8%, transparent)"
            strokeWidth="3"
          />
          <path
            d="M24 4 a20 20 0 0 1 20 20"
            stroke="var(--brand-green)"
            strokeWidth="3"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </div>

      <div className="flex flex-col items-center gap-2">
        <p className="text-foreground/80 text-sm font-medium">{label}</p>
        {!prefersReducedMotion && (
          <div className="flex items-center gap-1">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="block h-1 w-1 rounded-full"
                style={{ backgroundColor: "var(--brand-green)" }}
                animate={{ opacity: [0.25, 1, 0.25] }}
                transition={{ duration: 1, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
              />
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export function CategoryFilterSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      className="bg-card border-border overflow-hidden rounded-xl border"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="border-border border-b px-5 py-4">
        <h3 className="text-foreground text-lg font-semibold">Categories</h3>
      </div>
      <ul className="space-y-1 px-5 py-3">
        {/* eslint-disable react/no-array-index-key -- skeleton loader rows have no stable IDs */}
        {Array.from({ length: rows }).map((_, i) => (
          <li key={`cat-skeleton-${i}`} className="px-3 py-2">
            <span className="flex items-center gap-2">
              <span className="bg-foreground/6 h-3 w-3 animate-pulse rounded-sm" />
              <span
                className="bg-foreground/6 h-3 animate-pulse rounded"
                style={{ width: `${55 + ((i * 13) % 35)}%` }}
              />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
